'use client';
import React, { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  BarChart3,
  TrendingUp,
  Mail,
  FileText,
  CalendarPlus,
  CalendarDays,
  Users,
  ShoppingBag,
  ExternalLink,
  LogOut,
  Mountain,
  Newspaper,
  Syringe,
  Wind,
  MessageSquare,
  Link2,
  LucideIcon,
} from 'lucide-react';

// ─── Shortcut configuration ────────────────────────────────────────────────
type Shortcut = {
  title: string;
  description: string;
  href: string;
  icon: LucideIcon;
  color: string;
  external?: boolean;
};

const MTD_GROWTH: Shortcut[] = [
  { title: 'MTD Snapshot',  description: 'Monthly revenue & bookings', href: '/metrics/report6', icon: BarChart3,  color: 'bg-violet-500' },
  { title: 'Growth Report', description: 'Leads & conversion',         href: '/metrics/growth',  icon: TrendingUp, color: 'bg-emerald-500' },
];

const REPORTS: Shortcut[] = [
  { title: 'Consultations', description: 'Booking performance', href: '/metrics/consults',   icon: Users,      color: 'bg-sky-500' },
  { title: 'Orders',        description: 'Shop & revenue',      href: '/metrics/orders',     icon: ShoppingBag,color: 'bg-amber-500' },
  { title: 'Retreats',      description: 'Retreat sign-ups',    href: '/metrics/retreats',   icon: Mountain,   color: 'bg-orange-500' },
  { title: 'Newsletter',    description: 'Open & click rates',  href: '/metrics/newsletter', icon: Newspaper,  color: 'bg-pink-500' },
];

const CALENDARS: Shortcut[] = [
  { title: 'Calendars',      description: 'Upcoming clinic schedule',   href: '/metrics/calendars',     icon: CalendarDays,  color: 'bg-indigo-500' },
  { title: 'Body Ballancer', description: 'Lymphatic session bookings', href: '/metrics/bodyballancer', icon: Wind,          color: 'bg-purple-500' },
  { title: 'Enquiries',      description: 'Inbox & sent emails',        href: '/emailSummary',          icon: MessageSquare, color: 'bg-slate-500' },
  { title: 'Day Clinic',     description: 'IV / IM slot bookings',      href: '/metrics/dayclinic',     icon: Syringe,       color: 'bg-teal-500' },
];

const COMPOSE: Shortcut[] = [
  { title: 'Newsletter',  description: 'Compose & send',    href: '/newsletter/admin', icon: Mail,         color: 'bg-rose-600' },
  { title: 'Blog',        description: 'Write & edit posts',href: '/blog/admin',       icon: FileText,     color: 'bg-indigo-500' },
  { title: 'Short Links', description: 'Create & track cfow.uk links', href: '/account/links', icon: Link2, color: 'bg-cyan-600' },
  { title: 'Campaign Manager',    description: 'External Campaign',    href: 'https://admin.cfweb.yewly.co.uk/login', icon: ExternalLink, color: 'bg-gray-600', external: true },
];
// ──────────────────────────────────────────────────────────────────────────

function ShortcutCard({ title, description, href, icon: Icon, color, external }: Shortcut) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="group relative flex flex-col gap-3.5 bg-white rounded-2xl p-4 shadow-sm border border-black/5 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150"
    >
      <span className={`${color} w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm`}>
        <Icon size={18} strokeWidth={1.75} />
      </span>
      <div>
        <p className="font-semibold text-gray-900 text-sm leading-tight">{title}</p>
        <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">{description}</p>
      </div>
      {external && (
        <ExternalLink size={11} className="absolute top-3.5 right-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
      )}
    </a>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-3 px-0.5">
      {children}
    </h2>
  );
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const res = await fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    setLoading(false);

    if (res.ok) {
      const from = searchParams.get('from') || '/account';
      router.push(from);
      router.refresh();
    } else {
      setError('Invalid username or password');
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f4f1] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#9b6948] mb-1">CF Organic Wellness</p>
          <h1 className="text-2xl font-bold text-[#1a0d10]">Admin</h1>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className="block w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4F1922] focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="block w-full rounded-xl border border-gray-200 px-4 py-3 text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4F1922] focus:border-transparent text-sm"
              />
            </div>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-[#4F1922] text-white font-semibold rounded-xl hover:bg-[#3d1219] disabled:opacity-50 transition-colors text-sm mt-2"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const router = useRouter();

  const handleLogout = async () => {
    await fetch('/api/admin/logout', { method: 'POST' });
    router.push('/account');
    router.refresh();
  };

  return (
    <div className="min-h-screen bg-[#f7f4f1]">
      {/* Header */}
      <header className="bg-[#1a0d10] px-6 py-5 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#c4956a] mb-0.5">CF Organic Wellness</p>
          <h1 className="text-lg font-semibold text-white tracking-tight">Admin Hub</h1>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-sm text-[#a08070] hover:text-white transition-colors"
        >
          <LogOut size={14} strokeWidth={1.75} />
          Sign out
        </button>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">

        {/* Quick Book — featured hero card */}
        <div>
          <a
            href="/quickbook"
            className="group flex items-center gap-5 bg-[#1a0d10] rounded-2xl px-6 py-5 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150"
          >
            <span className="w-14 h-14 rounded-2xl bg-[#c4956a] flex items-center justify-center text-[#1a0d10] shadow-inner flex-shrink-0">
              <CalendarPlus size={26} strokeWidth={1.75} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-lg leading-tight">Quick Book</p>
              <p className="text-[#a08070] text-sm mt-0.5">Log a booking or follow-up</p>
            </div>
            <span className="text-[#c4956a] text-2xl font-light group-hover:translate-x-1 transition-transform">→</span>
          </a>
        </div>

        {/* MTD and Growth */}
        <div>
          <SectionHeading>MTD and Growth</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {MTD_GROWTH.map((s) => <ShortcutCard key={s.href} {...s} />)}
          </div>
        </div>

        {/* Reports */}
        <div>
          <SectionHeading>Reports</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {REPORTS.map((s) => <ShortcutCard key={s.href} {...s} />)}
          </div>
        </div>

        {/* Calendars */}
        <div>
          <SectionHeading>Calendars</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {CALENDARS.map((s) => <ShortcutCard key={s.href} {...s} />)}
          </div>
        </div>

        {/* Compose */}
        <div>
          <SectionHeading>Compose</SectionHeading>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {COMPOSE.map((s) => <ShortcutCard key={s.href} {...s} />)}
          </div>
        </div>

      </main>
    </div>
  );
}
